/**
 * Cartop checkout prefill helpers.
 * Prefills shipping + billing so Factuuradres works for non-shipping / deposit carts.
 * @see https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks
 */
(function (window) {
  'use strict';

  function trim(v) {
    return String(v == null ? '' : v).trim();
  }

  /** Parse "street, zip city" or "street, city, zip" style NL lines. */
  function parseDutchAddress(raw) {
    var s = trim(raw);
    if (!s) return { address1: '', city: '', zip: '' };

    var zipMatch = s.match(/\b(\d{4}\s*[A-Za-z]{2})\b/i);
    var zip = zipMatch ? zipMatch[1].replace(/\s+/g, ' ').toUpperCase() : '';

    var parts = s.split(',').map(function (p) {
      return trim(p);
    }).filter(Boolean);

    var address1 = parts[0] || s;
    var city = '';

    if (parts.length >= 2) {
      var rest = parts.slice(1).join(' ');
      if (zip) {
        city = trim(rest.replace(zipMatch[0], '').replace(/^[,\s]+|[,\s]+$/g, ''));
      } else {
        city = parts[parts.length - 1] || '';
      }
    } else if (zip) {
      city = trim(s.replace(zipMatch[0], '').replace(address1, '').replace(/^[,\s]+|[,\s]+$/g, ''));
    }

    if (zip && address1.indexOf(zip) !== -1) {
      address1 = trim(address1.replace(zip, '').replace(/[,\s]+$/g, ''));
    }

    return { address1: address1, city: city, zip: zip };
  }

  function splitName(full) {
    var parts = trim(full).split(/\s+/).filter(Boolean);
    if (!parts.length) return { firstName: '', lastName: '' };
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }

  function mergeData(base, extra) {
    var out = Object.assign({}, base || {});
    var src = extra || {};
    Object.keys(src).forEach(function (key) {
      if (trim(src[key]) && !trim(out[key])) out[key] = src[key];
    });
    return out;
  }

  function fromCartItemProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    var email = trim(properties._booking_email || properties.Email || properties.email);
    var phone = trim(properties._booking_phone || properties.Telefoon || properties.Phone || properties.phone);
    var nameParts = splitName(
      properties._booking_customer_name || properties.Naam || properties.Name || ''
    );
    var addressRaw = trim(
      properties.Bezoekadres ||
        properties._booking_address ||
        properties.Adres ||
        properties.Address ||
        ''
    );
    var parsed = parseDutchAddress(addressRaw);
    var city = trim(properties.Stad || properties.City || parsed.city);
    var zip = trim(properties.Postcode || properties.Zip || parsed.zip);
    var address1 = trim(properties.Straat || properties.Address1 || parsed.address1);

    return {
      email: email,
      phone: phone,
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      address1: address1,
      city: city,
      zip: zip,
      country: 'NL',
    };
  }

  async function dataFromCart() {
    try {
      var res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return {};
      var cart = await res.json();
      var merged = {};
      (cart.items || []).forEach(function (item) {
        merged = mergeData(merged, fromCartItemProperties(item.properties));
      });
      return merged;
    } catch (e) {
      return {};
    }
  }

  function hasPrefill(data) {
    if (!data) return false;
    return !!(
      trim(data.email) ||
      trim(data.firstName) ||
      trim(data.lastName) ||
      trim(data.phone) ||
      trim(data.address1) ||
      trim(data.city) ||
      trim(data.zip)
    );
  }

  var CartopCheckoutPrefill = {
    parseDutchAddress: parseDutchAddress,

    buildUrl: function (data) {
      var params = new URLSearchParams();
      var email = trim(data && data.email);
      var firstName = trim(data && data.firstName);
      var lastName = trim(data && data.lastName);
      var phone = trim(data && data.phone);
      var address1 = trim(data && data.address1);
      var address2 = trim(data && data.address2);
      var city = trim(data && data.city);
      var zip = trim(data && data.zip);
      var country = trim((data && data.country) || 'NL') || 'NL';

      if (email) params.set('checkout[email]', email);

      ['shipping_address', 'billing_address'].forEach(function (block) {
        if (firstName) params.set('checkout[' + block + '][first_name]', firstName);
        if (lastName) params.set('checkout[' + block + '][last_name]', lastName);
        if (phone) params.set('checkout[' + block + '][phone]', phone);
        if (address1) params.set('checkout[' + block + '][address1]', address1);
        if (address2) params.set('checkout[' + block + '][address2]', address2);
        if (city) params.set('checkout[' + block + '][city]', city);
        if (zip) params.set('checkout[' + block + '][zip]', zip);
        params.set('checkout[' + block + '][country]', country);
      });

      var qs = params.toString();
      return '/checkout' + (qs ? '?' + qs : '');
    },

    buildPermalink: function (variantId, quantity, data) {
      var base = '/cart/' + Number(variantId) + ':' + (Number(quantity) || 1);
      var checkoutPath = this.buildUrl(data || {});
      var qs = checkoutPath.indexOf('?') !== -1 ? checkoutPath.split('?')[1] : '';
      return qs ? base + '?' + qs : base;
    },

    /**
     * Merge window.CartopPrefillData with optional cart line-item booking props.
     */
    resolveData: async function () {
      var fromPage = window.CartopPrefillData || {};
      var fromCart = await dataFromCart();
      // Explicit page/session data wins over cart property fallbacks.
      return mergeData(fromCart, fromPage);
    },

    goToCheckout: async function () {
      var data = await this.resolveData();
      if (!hasPrefill(data)) {
        window.location.href = '/checkout';
        return;
      }
      window.location.href = this.buildUrl(data);
    },

    bindCheckoutButtons: function (root) {
      var scope = root || document;
      var buttons = scope.querySelectorAll(
        '#CartDrawer-Checkout, button.cart__checkout-button[name="checkout"], button[name="checkout"].cart__checkout-button'
      );

      buttons.forEach(function (btn) {
        if (btn.dataset.cartopPrefillBound === '1') return;
        btn.dataset.cartopPrefillBound = '1';

        btn.addEventListener(
          'click',
          function (e) {
            if (btn.disabled) return;
            e.preventDefault();
            e.stopPropagation();
            btn.disabled = true;
            CartopCheckoutPrefill.goToCheckout().finally(function () {
              // If navigation fails, re-enable
              setTimeout(function () {
                btn.disabled = false;
              }, 4000);
            });
          },
          true
        );
      });
    },
  };

  window.CartopCheckoutPrefill = CartopCheckoutPrefill;

  function boot() {
    CartopCheckoutPrefill.bindCheckoutButtons(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Cart drawer re-renders via section rendering — rebind after DOM updates.
  document.addEventListener('cart:updated', boot);
  document.addEventListener('cart-drawer:updated', boot);

  var obs = new MutationObserver(function () {
    CartopCheckoutPrefill.bindCheckoutButtons(document);
  });
  if (document.body) {
    obs.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      obs.observe(document.body, { childList: true, subtree: true });
    });
  }
})(window);
